/**
 * Query options for a board's automation rules, ordered by position. One cache
 * entry per tracker holds the whole list response; `trackerAutomationsQueryOptions`
 * selects just the rules, so callers that only count or scan them stay as they
 * were. Goes through the generated `TrackersService` (baseURL + auth applied).
 */
import { TrackersService } from '@inference.engine/workspace-api-client/web';
import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';

import { workspaceHttp } from '@/lib/api/http-client';

import type {
  PreviewTrackerAutomationDto,
  TrackerAiBudget,
  TrackerAiCapabilities,
  TrackerAvailableAgent,
  TrackerPerson,
  TrackerAutomationSuggestionPage,
  TrackerAutomationList,
  TrackerAutomationPreview,
  TrackerAutomationPreviewRows,
  TrackerAutomationRunPage,
} from '../model/types';
import { TRACKER_AUTOMATION_QUERY_KEYS } from './query-keys';

export const trackerAutomationListQueryOptions = (trackerId: string) =>
  queryOptions({
    queryKey: TRACKER_AUTOMATION_QUERY_KEYS.list(trackerId),
    queryFn: async ({ signal }): Promise<TrackerAutomationList> => {
      const response = await TrackersService.listAutomations({
        client: workspaceHttp,
        path: { trackerId },
        signal,
      });
      if (!response.data) throw new Error('Failed to list automations');
      return response.data;
    },
    enabled: Boolean(trackerId),
    staleTime: 30_000,
  });

export const trackerAutomationsQueryOptions = (trackerId: string) =>
  queryOptions({
    ...trackerAutomationListQueryOptions(trackerId),
    select: (list: TrackerAutomationList) => list.items,
  });

/** A board's run history, newest first, a page at a time. Narrow it to one rule or one row. */
export const trackerAutomationRunsQueryOptions = (
  trackerId: string,
  filter: { automationId?: string; rowRef?: string },
) =>
  infiniteQueryOptions({
    queryKey: TRACKER_AUTOMATION_QUERY_KEYS.runs(trackerId, filter),
    queryFn: async ({ pageParam, signal }): Promise<TrackerAutomationRunPage> => {
      const response = await TrackersService.listAutomationRuns({
        client: workspaceHttp,
        path: { trackerId },
        query: { ...filter, cursor: pageParam },
        signal,
      });
      if (!response.data) throw new Error('Failed to list automation runs');
      return response.data;
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (page) => page.nextCursor ?? undefined,
    enabled: Boolean(trackerId),
    staleTime: 10_000,
  });

/**
 * What a draft rule would do to the board, without changing it. Keyed on the
 * draft itself, so an edit asks again and an unchanged draft reads the cache.
 */
export const trackerAutomationPreviewQueryOptions = (
  trackerId: string,
  draft: PreviewTrackerAutomationDto,
) =>
  queryOptions({
    queryKey: TRACKER_AUTOMATION_QUERY_KEYS.preview(trackerId, draft),
    queryFn: async ({ signal }): Promise<TrackerAutomationPreview> => {
      const response = await TrackersService.previewAutomation({
        client: workspaceHttp,
        path: { trackerId },
        body: draft,
        signal,
      });
      if (!response.data) throw new Error('Failed to preview automation');
      return response.data;
    },
    enabled: Boolean(trackerId),
    staleTime: 10_000,
  });

/** Rows to pick one from to try a draft rule on, narrowed by title. */
export const trackerAutomationPreviewRowsQueryOptions = (trackerId: string, search: string) =>
  queryOptions({
    queryKey: TRACKER_AUTOMATION_QUERY_KEYS.previewRows(trackerId, search),
    queryFn: async ({ signal }): Promise<TrackerAutomationPreviewRows> => {
      const response = await TrackersService.listAutomationPreviewRows({
        client: workspaceHttp,
        path: { trackerId },
        query: search ? { search } : {},
        signal,
      });
      if (!response.data) throw new Error('Failed to list rows to preview on');
      return response.data;
    },
    enabled: Boolean(trackerId),
    staleTime: 10_000,
  });

/** The organization's monthly AI budget and its spend so far. Organization admins only; anyone else gets a 403. */
export const trackerAiBudgetQueryOptions = () =>
  queryOptions({
    queryKey: TRACKER_AUTOMATION_QUERY_KEYS.aiBudget(),
    queryFn: async ({ signal }): Promise<TrackerAiBudget> => {
      const response = await TrackersService.getOrganizationAiBudget({
        client: workspaceHttp,
        signal,
      });
      if (!response.data) throw new Error('Failed to read the AI budget');
      return response.data;
    },
    staleTime: 30_000,
    retry: false,
  });

/** Which AI actions the builder may offer the person in this workspace. */
export const trackerAiCapabilitiesQueryOptions = (workspaceId: string | null) =>
  queryOptions({
    queryKey: TRACKER_AUTOMATION_QUERY_KEYS.aiCapabilities(workspaceId),
    queryFn: async ({ signal }): Promise<TrackerAiCapabilities> => {
      const response = await TrackersService.getAiCapabilities({ client: workspaceHttp, signal });
      if (!response.data) throw new Error('Failed to read the AI capabilities');
      return response.data;
    },
    enabled: Boolean(workspaceId),
    staleTime: 60_000,
  });

const AVAILABLE_AGENTS_PAGE_SIZE = 100;

/**
 * The agents the person may run from a rule on this board, across every workspace they
 * can read, ordered by workspace name: agents without tools only. All pages of them.
 */
export const trackerAvailableAgentsQueryOptions = (trackerId: string) =>
  queryOptions({
    queryKey: TRACKER_AUTOMATION_QUERY_KEYS.availableAgents(trackerId),
    queryFn: async ({ signal }): Promise<TrackerAvailableAgent[]> => {
      const agents: TrackerAvailableAgent[] = [];
      let cursor: string | undefined;
      do {
        const response = await TrackersService.listAvailableAgentsForBoard({
          client: workspaceHttp,
          path: { trackerId },
          query: { pageSize: AVAILABLE_AGENTS_PAGE_SIZE, ...(cursor ? { cursor } : {}) },
          signal,
        });
        if (!response.data) throw new Error('Failed to list the available agents');
        agents.push(...response.data.data);
        cursor = response.data.hasMore ? (response.data.nextCursor ?? undefined) : undefined;
      } while (cursor);
      return agents;
    },
    enabled: Boolean(trackerId),
    staleTime: 60_000,
  });

const PEOPLE_PAGE_SIZE = 100;

/** The organization's people directory a rule may name: assignees, recipients, mentions. All pages of it. */
export const trackerPeopleQueryOptions = () =>
  queryOptions({
    queryKey: TRACKER_AUTOMATION_QUERY_KEYS.people(),
    queryFn: async ({ signal }): Promise<TrackerPerson[]> => {
      const people: TrackerPerson[] = [];
      let cursor: string | undefined;
      do {
        const response = await TrackersService.listTrackerPeople({
          client: workspaceHttp,
          query: { pageSize: PEOPLE_PAGE_SIZE, ...(cursor ? { cursor } : {}) },
          signal,
        });
        if (!response.data) throw new Error('Failed to list people');
        people.push(...response.data.data);
        cursor = response.data.hasMore ? (response.data.nextCursor ?? undefined) : undefined;
      } while (cursor);
      return people;
    },
    staleTime: 60_000,
  });

/** Rules the board's own repeated edits suggest, strongest first. Owners and admins only. */
export const trackerAutomationSuggestionsQueryOptions = (trackerId: string) =>
  queryOptions({
    queryKey: TRACKER_AUTOMATION_QUERY_KEYS.suggestions(trackerId),
    queryFn: async ({ signal }): Promise<TrackerAutomationSuggestionPage> => {
      const response = await TrackersService.listAutomationSuggestions({
        client: workspaceHttp,
        path: { trackerId },
        signal,
      });
      if (!response.data) throw new Error('Failed to list rule suggestions');
      return response.data;
    },
    enabled: Boolean(trackerId),
    staleTime: 60_000,
    retry: false,
  });
